import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CopyIcon, CheckIcon, RefreshCw, User, EditIcon, SaveIcon, XIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useBrokerReferralValidation } from '@/hooks/broker/useBrokerReferralValidation';
import { useBroker } from '@/hooks/use-broker';
import { sanitizeReferralCode } from '@/utils/referralCodeUtils';

export const BrokerReferralCodeField = () => {
  const { brokerProfile, updateBrokerProfile } = useBroker();
  const { validateReferralCode, generateReferralCode, generateCodeFromName, isValidating } = useBrokerReferralValidation();
  const { toast } = useToast();
  
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [copied, setCopied] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleCopyReferralCode = () => {
    if (!brokerProfile?.referral_code) return;
    
    navigator.clipboard.writeText(brokerProfile.referral_code);
    setCopied(true);
    toast({
      title: "Referral code copied",
      description: "The referral code has been copied to your clipboard"
    });
    
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStartEdit = () => {
    setEditValue(brokerProfile?.referral_code || '');
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditValue('');
  };

  const handleSaveEdit = async () => {
    if (!brokerProfile) return;

    const sanitizedCode = sanitizeReferralCode(editValue);
    
    // Validate the code
    const isValid = await validateReferralCode(sanitizedCode, brokerProfile.id);
    if (!isValid) return;

    setIsSaving(true);
    try {
      await updateBrokerProfile({ referral_code: sanitizedCode });
      setIsEditing(false);
      setEditValue('');
      toast({
        title: "Referral Code Updated",
        description: "Your referral code has been successfully updated.",
      });
    } catch (error) {
      console.error('Error updating referral code:', error);
      toast({
        title: "Update Failed",
        description: "Failed to update referral code. Please try again.",
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleGenerateRandom = async () => {
    const newCode = await generateReferralCode();
    if (newCode) {
      setEditValue(newCode);
    }
  };

  const handleGenerateFromName = async () => {
    if (!brokerProfile?.company_name) {
      toast({
        title: "Company Name Required",
        description: "Please set your company name in your profile first.",
        variant: "destructive",
      });
      return;
    }

    const newCode = await generateCodeFromName(brokerProfile.company_name);
    if (newCode) {
      setEditValue(newCode);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Your Referral Code</CardTitle>
      </CardHeader>
      <CardContent>
        {isEditing ? (
          <div className="space-y-3">
            <div className="flex space-x-2 items-center">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder="Enter your referral code"
                className="font-mono"
              />
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleSaveEdit}
                disabled={isSaving || isValidating}
                className="shrink-0"
              >
                <SaveIcon className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                size="icon" 
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="shrink-0"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
            <div className="flex space-x-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateFromName}
                disabled={isValidating || !brokerProfile?.company_name}
                title="Generate referral code from company name"
              >
                <User className="h-4 w-4 mr-1" />
                From Company Name
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateRandom}
                disabled={isValidating}
                title="Generate random referral code"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isValidating ? 'animate-spin' : ''}`} />
                Random
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex space-x-2 items-center">
            <Input
              value={brokerProfile?.referral_code || ''}
              readOnly
              className="font-mono bg-muted"
            />
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleStartEdit}
              className="shrink-0"
              title="Edit referral code"
            >
              <EditIcon className="h-4 w-4" />
            </Button>
            <Button 
              variant="outline" 
              size="icon" 
              onClick={handleCopyReferralCode}
              className="shrink-0"
              title="Copy referral code"
            >
              {copied ? <CheckIcon className="h-4 w-4" /> : <CopyIcon className="h-4 w-4" />}
            </Button>
          </div>
        )}
        <p className="text-sm text-muted-foreground mt-2">
          Share this code with your clients so they can link their account to you
        </p>
      </CardContent>
    </Card>
  );
};